import React, { useState, useEffect } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../components/Header';
import GlassCard from '../components/GlassCard';
import { FaFilter, FaTimes, FaSearch, FaStar } from 'react-icons/fa';

// Mobil breakpoint
const MOBILE_BREAKPOINT = 768;

const DiscoverContainer = styled(motion.div)`
  padding: 100px 5% 50px 5%;
  min-height: 100vh;
  background: var(--background-start);
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 10px;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--secondary-color);
  margin-bottom: 30px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 25px;
  background: var(--gradient-pink);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 30px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(255, 0, 127, 0.3);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 10px 20px;
    font-size: 0.9rem;
  }
`;

const HeaderSearchContainer = styled.div`
  position: absolute;
  top: 100px;
  right: 5%;
  width: 300px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: static;
    width: 100%;
    margin-bottom: 20px;
  }
`;

const HeaderSearchInput = styled.div`
  position: relative;
  width: 100%;

  input {
    width: 100%;
    padding: 12px 45px 12px 15px;
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    color: var(--primary-color);
    font-size: 0.95rem;
    backdrop-filter: blur(10px);
    transition: all 0.3s ease;

    &:focus {
      border-color: var(--gradient-pink);
      outline: none;
      box-shadow: 0 0 0 2px rgba(255, 0, 127, 0.1);
    }

    &::placeholder {
      color: var(--secondary-color);
    }
  }

  svg {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--secondary-color);
    font-size: 1rem;
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 25px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px;
    max-height: 80vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.3s ease;

  &:hover {
    background: var(--gradient-pink);
    color: white;
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 20px;
  color: var(--primary-color);
  text-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1.5rem;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 25px;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 12px;
  color: var(--primary-color);
  border-bottom: 2px solid var(--gradient-pink);
  padding-bottom: 5px;
  display: inline-block;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 15px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 8px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--gradient-cyan);
    transform: translateY(-2px);
  }

  &.selected {
    border-color: var(--gradient-pink);
    background: rgba(255, 0, 127, 0.1);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 8px 10px;
  }
`;

const FilterCheckbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--gradient-pink);
`;

const FilterLabel = styled.label`
  color: var(--primary-color);
  font-size: 0.85rem;
  cursor: pointer;
  flex: 1;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 0.8rem;
  }
`;

const SearchInput = styled.div`
  position: relative;
  margin-bottom: 15px;

  input {
    width: 100%;
    padding: 10px 40px 10px 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--primary-color);
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: var(--gradient-pink);
      outline: none;
    }
  }

  svg {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--secondary-color);
    font-size: 0.9rem;
  }
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--gradient-pink);
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(255, 0, 127, 0.4);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 10px;
    font-size: 0.9rem;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 25px;
  margin-top: 25px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 15px;
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px 18px;
  background: var(--glass-bg);
  border-radius: 10px;
  border: 1px solid var(--border-color);
  font-size: 0.9rem;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
    padding: 10px 15px;
  }
`;

const ResultsCount = styled.span`
  color: var(--primary-color);
  font-weight: 600;
`;

const ActiveFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActiveFilterTag = styled.span`
  background: var(--gradient-pink);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--secondary-color);
  font-size: 1rem;
`;

const YearSelector = styled.div`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 15px;
`;

const YearGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin: 10px 0;
  max-height: 120px;
  overflow-y: auto;
`;

const YearButton = styled.button`
  padding: 6px 12px;
  background: ${props => props.$active ? 'var(--gradient-pink)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active ? 'var(--gradient-pink)' : 'var(--border-color)'};
  border-radius: 6px;
  color: ${props => props.$active ? 'white' : 'var(--primary-color)'};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--gradient-pink);
    color: ${props => props.$active ? 'white' : 'var(--gradient-pink)'};
  }
`;

const YearLabel = styled.div`
  font-size: 0.8rem;
  color: var(--secondary-color);
  text-align: center;
  margin-top: 10px;
`;

const DiscoverPage = () => {
  const [siteContent, setSiteContent] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [mediaType, setMediaType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchTerm, setHeaderSearchTerm] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startYear, setStartYear] = useState('');
  const [endYear, setEndYear] = useState('');
  const [dbCategories, setDbCategories] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  // Mobil tespiti
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // MySQL'den tüm içerikleri çek
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_CONTENT));
        
        if (!response.ok) {
          throw new Error('İçerikler çekilemedi');
        }
        
        const data = await response.json();
        setSiteContent(data);
        setFilteredItems(data);
      } catch (error) {
        console.error('İçerik yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Database'den kategorileri çek
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.GET_CATEGORIES));
        
        if (!response.ok) {
          throw new Error('Kategoriler çekilemedi');
        }
        
        const data = await response.json();
        if (data.success) {
          setDbCategories(data.categories);
        }
      } catch (error) {
        console.error('Kategoriler yüklenirken hata:', error);
      }
    };

    fetchCategories();
  }, []);

  // Filtreleme işlemi
  useEffect(() => {
    let filtered = siteContent;

    // Medya türüne göre filtrele
    if (mediaType !== 'all') {
      filtered = filtered.filter(item => item.media_type === mediaType);
    }

    // Header arama terimine göre filtrele
    if (headerSearchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(headerSearchTerm.toLowerCase()) ||
        item.original_title?.toLowerCase().includes(headerSearchTerm.toLowerCase())
      );
    }

    // Modal içi arama terimine göre filtrele
    if (searchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.original_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.overview?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Rating'e göre filtrele
    if (minRating > 0) {
      filtered = filtered.filter(item => (item.vote_average || 0) >= minRating);
    }

    // Yıl aralığına göre filtrele
    if (startYear) {
      filtered = filtered.filter(item => {
        const releaseYear = item.release_date ? new Date(item.release_date).getFullYear() : 0;
        return releaseYear >= parseInt(startYear);
      });
    }

    if (endYear) {
      filtered = filtered.filter(item => {
        const releaseYear = item.release_date ? new Date(item.release_date).getFullYear() : 0;
        return releaseYear <= parseInt(endYear);
      });
    }

    // Kategori filtreleme - API'den gelen categories alanını kullan
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(item => {
        // API'den gelen categories string'ini kontrol et
        if (item.categories && typeof item.categories === 'string') {
          const itemCategories = item.categories.toLowerCase();
          return selectedCategories.some(selectedCat => 
            itemCategories.includes(selectedCat.toLowerCase())
          );
        }
        
        // Fallback: title ve overview'da ara
        const searchText = [
          item.title?.toLowerCase(),
          item.overview?.toLowerCase(),
          item.original_title?.toLowerCase()
        ].filter(Boolean).join(' ');
        
        return selectedCategories.some(cat => 
          searchText.includes(cat.toLowerCase())
        );
      });
    }

    setFilteredItems(filtered);
  }, [siteContent, mediaType, searchTerm, headerSearchTerm, minRating, selectedCategories, startYear, endYear]);

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(cat => cat !== category)
        : [...prev, category]
    );
  };

  const clearFilters = () => {
    setMediaType('all');
    setSearchTerm('');
    setHeaderSearchTerm('');
    setMinRating(0);
    setSelectedCategories([]);
    setCategorySearch('');
    setStartYear('');
    setEndYear('');
  };

  const applyFilters = () => {
    setIsModalOpen(false);
  };

  // Database'den gelen kategorileri filtrele
  const filteredCategories = dbCategories.filter(cat =>
    cat.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  // Sitedeki benzersiz medya türlerini bul
  const availableMediaTypes = [...new Set(siteContent.map(item => item.media_type))];

  // Medya türü etiketini formatla
  const getMediaTypeLabel = (type) => {
    switch(type) {
      case 'movie': return 'Film';
      case 'tv': return 'Dizi';
      case 'anime': return 'Anime';
      default: return type;
    }
  };

  // Yıl seçenekleri
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);

  const getImageUrl = (path, size = 'w500') => {
    if (!path || path === 'null' || path === 'undefined' || (typeof path === 'string' && path.trim() === '')) {
      return '/placeholder-image.jpg';
    }
    
    if (path.includes('/proxy-image.php')) return path;

    // Eğer path tam bir TMDB URL'si ise, sadece path kısmını çıkar
    if (typeof path === 'string' && path.includes('image.tmdb.org')) {
      const match = path.match(/\/t\/p\/[^\/]+\/(.+)$/);
      if (match) {
        path = '/' + match[1];
      }
    }

    // Path'i normalize et - başında / olmalı
    let normalizedPath = typeof path === 'string' ? path.trim() : String(path);
    if (!normalizedPath.startsWith('/') && !normalizedPath.startsWith('http')) {
      normalizedPath = '/' + normalizedPath;
    }

    // Tek bir proxy yolu kullanıyoruz
    return `${getApiUrl(API_ENDPOINTS.PROXY_IMAGE)}?path=${encodeURIComponent(normalizedPath)}&size=${size}`;
  };


  return (
    <DiscoverContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Header />
      
      <Title>Keşfet</Title>
      <Subtitle>Sitemizdeki {siteContent.length} içerik arasında keşfe çıkın</Subtitle>

      <HeaderSearchContainer>
        <HeaderSearchInput>
          <input
            type="text"
            placeholder="İçerik ara..."
            value={headerSearchTerm}
            onChange={(e) => setHeaderSearchTerm(e.target.value)}
          />
          <FaSearch />
        </HeaderSearchInput>
      </HeaderSearchContainer>

      <FilterButton onClick={() => setIsModalOpen(true)}>
        <FaFilter />
        Filtrele ({filteredItems.length})
      </FilterButton>

      <ResultsInfo>
        <ResultsCount>
          {filteredItems.length} içerik
        </ResultsCount>
        <ActiveFilters>
          {mediaType !== 'all' && (
            <ActiveFilterTag>
              {getMediaTypeLabel(mediaType)}
            </ActiveFilterTag>
          )}
          {headerSearchTerm && (
            <ActiveFilterTag>
              "{headerSearchTerm}"
            </ActiveFilterTag>
          )}
          {searchTerm && (
            <ActiveFilterTag>
              Ara: "{searchTerm}"
            </ActiveFilterTag>
          )}
          {minRating > 0 && (
            <ActiveFilterTag>
              ⭐ {minRating}+
            </ActiveFilterTag>
          )}
          {(startYear || endYear) && (
            <ActiveFilterTag>
              {startYear || '1900'} - {endYear || currentYear}
            </ActiveFilterTag>
          )}
          {selectedCategories.length > 0 && (
            <ActiveFilterTag>
              {selectedCategories.length} kategori
            </ActiveFilterTag>
          )}
        </ActiveFilters>
      </ResultsInfo>

      {loading ? (
        <LoadingText>İçerikler yükleniyor...</LoadingText>
      ) : (
        <CardsGrid>
          {filteredItems.map(item => (
            <GlassCard
              key={item.id}
              title={item.title}
              imageUrl={getImageUrl(item.poster_path)}
              rating={item.vote_average}
              releaseDate={item.release_date}
              mediaTypeLabel={getMediaTypeLabel(item.media_type)}
              isMobile={isMobile}
              linkTo={`/${item.media_type}/${item.tmdb_id}`}
            />
          ))}
        </CardsGrid>
      )}

      <AnimatePresence>
        {isModalOpen && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <ModalContent
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={() => setIsModalOpen(false)}>
                <FaTimes />
              </CloseButton>

              <ModalTitle>Filtreler</ModalTitle>

              <FilterSection>
                <SectionTitle>Medya Türü</SectionTitle>
                <FilterGrid>
                  <FilterItem 
                    className={mediaType === 'all' ? 'selected' : ''}
                    onClick={() => setMediaType('all')}
                  >
                    <FilterCheckbox 
                      type="radio" 
                      checked={mediaType === 'all'}
                      readOnly
                    />
                    <FilterLabel>Tümü ({siteContent.length})</FilterLabel>
                  </FilterItem>
                  {availableMediaTypes.map(type => (
                    <FilterItem 
                      key={type}
                      className={mediaType === type ? 'selected' : ''}
                      onClick={() => setMediaType(type)}
                    >
                      <FilterCheckbox 
                        type="radio" 
                        checked={mediaType === type}
                        readOnly
                      />
                      <FilterLabel>
                        {getMediaTypeLabel(type)} 
                        ({siteContent.filter(item => item.media_type === type).length})
                      </FilterLabel>
                    </FilterItem>
                  ))}
                </FilterGrid>
              </FilterSection>

              <FilterSection>
                <SectionTitle>Arama</SectionTitle>
                <SearchInput>
                  <input
                    type="text"
                    placeholder="İçerik adı veya açıklama ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FaSearch />
                </SearchInput>
              </FilterSection>

              <FilterSection>
                <SectionTitle>Yıl Aralığı</SectionTitle>
                <div style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  alignItems: 'center',
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <YearSelector style={{ width: isMobile ? '100%' : 'auto' }}>
                    <YearButton 
                      onClick={() => setStartYear('')}
                      $active={startYear === ''}
                    >
                      Tümü
                    </YearButton>
                    <YearGrid>
                      {years.slice(0, 20).map(year => (
                        <YearButton
                          key={year}
                          onClick={() => setStartYear(year.toString())}
                          $active={startYear === year.toString()}
                        >
                          {year}
                        </YearButton>
                      ))}
                    </YearGrid>
                    <YearLabel>Başlangıç: {startYear || 'Seçilmemiş'}</YearLabel>
                  </YearSelector>
                  
                  <span style={{ 
                    color: 'var(--secondary-color)', 
                    fontSize: '1.5rem', 
                    fontWeight: 'bold',
                    transform: isMobile ? 'rotate(90deg)' : 'none',
                    margin: isMobile ? '10px 0' : '0'
                  }}>-</span>
                  
                  <YearSelector style={{ width: isMobile ? '100%' : 'auto' }}>
                    <YearButton 
                      onClick={() => setEndYear('')}
                      $active={endYear === ''}
                    >
                      Tümü
                    </YearButton>
                    <YearGrid>
                      {years.slice(0, 20).map(year => (
                        <YearButton
                          key={year}
                          onClick={() => setEndYear(year.toString())}
                          $active={endYear === year.toString()}
                        >
                          {year}
                        </YearButton>
                      ))}
                    </YearGrid>
                    <YearLabel>Bitiş: {endYear || 'Seçilmemiş'}</YearLabel>
                  </YearSelector>
                </div>
              </FilterSection>

              <FilterSection>
                <SectionTitle>Kategoriler</SectionTitle>
                <SearchInput>
                  <input
                    type="text"
                    placeholder="Kategori ara..."
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                  />
                  <FaSearch />
                </SearchInput>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '10px' }}>
                  <FilterGrid>
                    {filteredCategories.map(category => (
                      <FilterItem 
                        key={category.name}
                        className={selectedCategories.includes(category.name) ? 'selected' : ''}
                        onClick={() => toggleCategory(category.name)}
                      >
                        <FilterCheckbox 
                          type="checkbox"
                          checked={selectedCategories.includes(category.name)}
                          readOnly
                        />
                        <FilterLabel>{category.name} ({category.count})</FilterLabel>
                      </FilterItem>
                    ))}
                  </FilterGrid>
                </div>
              </FilterSection>

              <FilterSection>
                <SectionTitle>Değerlendirme</SectionTitle>
                <FilterGrid>
                  {[0, 5, 6, 7, 8].map(rating => (
                    <FilterItem 
                      key={rating}
                      className={minRating === rating ? 'selected' : ''}
                      onClick={() => setMinRating(rating)}
                    >
                      <FilterCheckbox 
                        type="radio"
                        checked={minRating === rating}
                        readOnly
                      />
                      <FilterLabel>
                        {rating === 0 ? 'Tümü' : `⭐ ${rating}+`}
                      </FilterLabel>
                    </FilterItem>
                  ))}
                </FilterGrid>
              </FilterSection>

              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <ApplyButton onClick={applyFilters}>
                  Filtreleri Uygula
                </ApplyButton>
                <button 
                  onClick={clearFilters}
                  style={{
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    borderRadius: '10px',
                    color: 'var(--primary-color)',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    flex: 1
                  }}
                >
                  Temizle
                </button>
              </div>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </DiscoverContainer>
  );
};

export default DiscoverPage;
